import * as React from "react";
import { motion, useReducedMotion, type Transition } from "framer-motion";
import { navigateTo } from "../../utils/navigation";

export default function A10ChecklistStart({
  className = "",
  onStart,
  startHref = "/new",
}: {
  className?: string;
  onStart?: () => void;
  startHref?: string;
}) {
  const prefersReduced = useReducedMotion();

  const inView: Transition = prefersReduced
    ? { duration: 0 }
    : { type: "spring", stiffness: 320, damping: 28 };

  const float: Transition = prefersReduced
    ? { duration: 0 }
    : { duration: 2.8, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" };

  const [checks, setChecks] = React.useState<Record<Key, boolean>>({
    light: false,
    background: false,
    lens: false,
    faceClear: false,
    framing: false,
  });

  const allChecked = Object.values(checks).every(Boolean);

  const items: Item[] = [
    {
      key: "light",
      title: "Even, front lighting",
      hint: "Stand facing a window or soft light — avoid strong shadows.",
      Icon: () => <SunFrontIcon float={float} />,
    },
    {
      key: "background",
      title: "Neutral background",
      hint: "Use a plain wall or simple backdrop to reduce visual noise.",
      Icon: () => <BackgroundIcon />,
    },
    {
      key: "lens",
      title: "Clean lens",
      hint: "Wipe the camera lens to avoid blur or haze.",
      Icon: () => <LensClothIcon float={float} />,
    },
    {
      key: "faceClear",
      title: "Face unobstructed",
      hint: "Pull hair back and keep your face fully visible.",
      Icon: () => <FaceClearIcon />,
    },
    {
      key: "framing",
      title: "Eye-level framing",
      hint: "Hold the camera at eye height, arm's length, face centered.",
      Icon: () => <FramingIcon />,
    },
  ];

  const toggle = (k: Key) => { setChecks((s) => ({ ...s, [k]: !s[k] })); };

  const handleStart = () => {
    if (onStart) { onStart(); return; }
    try {
      navigateTo(startHref);
    } catch {
      window.location.assign(startHref);
    }
  };

  return (
    <section
      className={["rounded-2xl p-5 sm:p-6", className].join(" ")}
      style={{ border: '1px solid var(--rule)', background: 'var(--c-cream)', boxShadow: 'var(--sh-lifted)' }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>Ready to scan?</h2>
        <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Tick the checklist and start when you&apos;re good to go.</p>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[340px,1fr]">
        {/* Illustration card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={inView}
          className="relative rounded-xl p-4 overflow-hidden"
          style={{ border: '1px solid var(--rule)', background: 'var(--c-sand)' }}
        >
          <div className="text-sm font-medium" style={{ color: 'var(--ink)' }}>Ideal setup</div>
          <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>Front light • Eye-level • Neutral backdrop</div>

          <div className="mt-4 grid place-items-center">
            <PhonePose float={float} />
          </div>

          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-64 h-16 blur-2xl rounded-full" style={{ background: 'rgba(31,36,33,.05)' }} />

          <AnimateConfetti show={allChecked && !prefersReduced} />
        </motion.div>

        {/* Checklist */}
        <motion.ul
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={inView}
          className="grid gap-2"
        >
          {items.map((it) => {
            const active = checks[it.key];
            return (
              <li key={it.key}>
                <button
                  type="button"
                  onClick={() => { toggle(it.key); }}
                  className="w-full rounded-xl p-3 text-left grid grid-cols-[44px,1fr,28px] items-center gap-3"
                  style={{
                    border: `1px solid ${active ? 'rgba(42,164,166,.35)' : 'var(--rule)'}`,
                    background: active ? 'rgba(42,164,166,.07)' : 'transparent',
                    transition: 'border-color 0.15s ease, background 0.15s ease',
                  }}
                  aria-pressed={active}
                >
                  <div className="h-11 w-11 rounded-lg grid place-items-center" style={{ background: 'rgba(31,36,33,.05)' }}>
                    <it.Icon />
                  </div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{it.title}</div>
                    <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>{it.hint}</div>
                  </div>
                  <Tick active={active} />
                </button>
              </li>
            );
          })}

          <div className="mt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="button"
              onClick={handleStart}
              disabled={!allChecked}
              className="rounded-lg px-4 py-2 text-sm font-semibold"
              style={{
                background: allChecked ? 'var(--c-deep-teal)' : 'rgba(31,36,33,.20)',
                color: allChecked ? '#F8F4EE' : 'rgba(31,36,33,.40)',
                cursor: allChecked ? 'pointer' : 'not-allowed',
                transition: 'background 0.15s ease',
              }}
            >
              {allChecked ? "Start scan" : "Complete checklist to start"}
            </button>

            <button
              type="button"
              onClick={handleStart}
              className="text-sm font-medium"
              style={{ color: 'var(--ink-muted)' }}
            >
              Start anyway
            </button>

            <button
              type="button"
              onClick={() =>
                { setChecks({ light: false, background: false, lens: false, faceClear: false, framing: false }); }
              }
              className="sm:ml-auto text-sm font-medium"
              style={{ color: 'var(--ink-muted)' }}
              title="Clear all ticks"
            >
              Reset
            </button>
          </div>

          <p className="mt-3 text-[11px] italic" style={{ color: 'var(--ink-muted)' }}>
            For cosmetic & educational use only. Photos are analyzed for appearance-based insights, not medical purposes.
          </p>

          <p className="mt-1 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
            Tip: If your lighting changes between shots, re-check &quot;Even, front lighting&quot; before continuing.
          </p>
        </motion.ul>
      </div>
    </section>
  );
}

type Key = "light" | "background" | "lens" | "faceClear" | "framing";
type Item = { key: Key; title: string; hint: string; Icon: React.FC };

function Tick({ active }: { active: boolean }) {
  return (
    <span
      className="inline-grid place-items-center h-7 w-7 rounded-full"
      style={{
        background: active ? 'var(--c-kivo-teal)' : 'transparent',
        border: `1.5px solid ${active ? 'var(--c-kivo-teal)' : 'var(--rule)'}`,
        transition: 'background 0.15s ease, border-color 0.15s ease',
      }}
      aria-hidden
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path
          d="M6 12.5l4 4 8-8"
          stroke={active ? "#F8F4EE" : "transparent"}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function PhonePose({ float }: { float: Transition }) {
  return (
    <div className="relative">
      <motion.svg
        width="180"
        height="160"
        viewBox="0 0 180 160"
        initial={{ y: 2 }}
        animate={{ y: -2 }}
        transition={float}
        aria-hidden
      >
        <rect x="18" y="18" width="144" height="92" rx="10" fill="#F8F4EE" stroke="rgba(31,36,33,.12)" />
        <g stroke="rgba(244,168,142,.28)" strokeLinecap="round" opacity="0.8">
          <line x1="90" y1="20" x2="90" y2="10" />
          <line x1="65" y1="26" x2="58" y2="18" />
          <line x1="115" y1="26" x2="122" y2="18" />
        </g>

        <circle cx="90" cy="56" r="18" fill="rgba(244,168,142,.28)" />
        <rect x="74" y="76" width="32" height="28" rx="8" fill="rgba(42,164,166,.22)" />
        <g transform="translate(118,50)">
          <rect x="0" y="0" width="24" height="40" rx="5" fill="rgba(31,36,33,.12)" stroke="rgba(31,36,33,.16)" />
          <circle cx="12" cy="10" r="2" fill="rgba(31,36,33,.28)" />
        </g>
        <rect x="60" y="36" width="60" height="60" rx="8" fill="none" stroke="rgba(42,164,166,.55)" strokeDasharray="6 6" />
      </motion.svg>

      <div className="mx-auto h-1.5 w-36 rounded-full blur-sm" style={{ background: 'rgba(31,36,33,.05)' }} />
    </div>
  );
}

function AnimateConfetti({ show }: { show: boolean }) {
  if (!show) return null;
  const colors = [
    'rgba(42,164,166,.80)',
    'rgba(244,168,142,.80)',
    'rgba(26,111,112,.70)',
    'rgba(239,231,220,.90)',
    'rgba(244,168,142,.60)',
    'rgba(42,164,166,.50)',
  ];
  return (
    <div className="pointer-events-none absolute inset-0">
      {[...Array.from({ length: 10 }, (_, i) => i)].map((i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: -6, scale: 0.9, rotate: 0, x: 0 }}
          animate={{ opacity: 1, y: -18 - i * 2, scale: 1, rotate: 20 + i * 12, x: (i - 5) * 6 }}
          transition={{ duration: 0.9 + i * 0.03, ease: "easeOut" }}
          className="absolute left-1/2 top-6 h-2 w-2 rounded-sm"
          style={{ background: colors[i % 6] }}
        />
      ))}
    </div>
  );
}

function SunFrontIcon({ float }: { float: Transition }) {
  return (
    <motion.svg width="28" height="28" viewBox="0 0 36 36" initial={{ y: 0.5 }} animate={{ y: -0.5 }} transition={float} aria-hidden>
      <circle cx="18" cy="18" r="7" fill="rgba(244,168,142,.80)" />
      <g stroke="rgba(244,168,142,.70)" strokeLinecap="round">
        <line x1="18" y1="3" x2="18" y2="7" />
        <line x1="18" y1="29" x2="18" y2="33" />
        <line x1="3" y1="18" x2="7" y2="18" />
        <line x1="29" y1="18" x2="33" y2="18" />
      </g>
    </motion.svg>
  );
}

function BackgroundIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 36 36" aria-hidden>
      <rect x="5" y="7" width="26" height="22" rx="4" fill="#F8F4EE" stroke="rgba(31,36,33,.12)" />
      <g fill="rgba(31,36,33,.12)">
        {[0, 1, 2].map((r) =>
          [0, 1, 2, 3].map((c) => <rect key={`${r}-${c}`} x={8 + c * 6} y={10 + r * 6} width="4" height="4" rx="1.3" />)
        )}
      </g>
    </svg>
  );
}

function LensClothIcon({ float }: { float: Transition }) {
  return (
    <motion.svg width="28" height="28" viewBox="0 0 36 36" initial={{ rotate: -2 }} animate={{ rotate: 2 }} transition={float} aria-hidden>
      <circle cx="18" cy="18" r="7" fill="rgba(42,164,166,.18)" stroke="rgba(42,164,166,.40)" />
      <path d="M11,23 Q18,16 25,23" fill="none" stroke="rgba(42,164,166,.55)" strokeWidth="2" />
    </motion.svg>
  );
}

function FaceClearIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 36 36" aria-hidden>
      <circle cx="18" cy="14" r="7" fill="rgba(244,168,142,.28)" />
      <rect x="12" y="22" width="12" height="6" rx="3" fill="rgba(244,168,142,.28)" />
      <path d="M9,9 L27,27" stroke="#2AA4A6" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function FramingIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 36 36" aria-hidden>
      <rect x="8" y="8" width="20" height="20" rx="3" fill="none" stroke="rgba(42,164,166,.55)" strokeDasharray="5 5" />
      <circle cx="18" cy="18" r="5" fill="rgba(42,164,166,.25)" />
    </svg>
  );
}
